import React, { useMemo } from 'react';
import { styled } from '@mui/material';
import {
    mdiCalendarRange,
    mdiCurrencyUsd,
    mdiDomain,
    mdiDrawPen,
    mdiFactory,
    mdiPackageVariantClosed,
    mdiTruckCargoContainer,
} from '@mdi/js';

import { useAppSelector } from '../../state/hooks';
import { CarrierBusinessUnit, Commodity, DeliveryParty, EquipmentType, PickupParty } from '../../interfaces/filters';
import { apiDateToLocalDate } from '../../utils/dateUtils';
import { truncateId } from '../../utils/formattingUtils';
import { getFilterValuesFromParams } from '../../utils/stateHelpers';
import { useFilters } from './useFilters';
import ActiveFilterChip from './ActiveFilterChip';

const classesPrefix = 'activeFilters';

const classes = {
    filtersDisplay: `${classesPrefix}-filtersDisplay`,
};

const StyledDiv = styled('div')(({ theme }) => {
    return {
        [`&.${classes.filtersDisplay}`]: {
            margin: theme.spacing(1),
            marginRight: 0,
            marginLeft: theme.spacing(2),
            display: 'flex',
            flexWrap: 'wrap',
        },
    };
});

const ActiveFilters = (): React.JSX.Element => {
    const filters = useFilters();

    const filterOptions = useAppSelector((state) => { return state.filters.filterOptions; });

    const selectedCarriers = useMemo((): CarrierBusinessUnit[] => {
        return getFilterValuesFromParams(
            filterOptions.carrierBusinessUnits,
            'carrier_business_unit_code',
            filters.carrier_business_unit_codes
        );
    }, [filterOptions.carrierBusinessUnits, filters.carrier_business_unit_codes]);

    const selectedEquipmentTypes = useMemo((): EquipmentType[] => {
        return getFilterValuesFromParams(
            filterOptions.equipmentTypes,
            'freight_order_specified_eqpt_code',
            filters.freight_order_specified_eqpt_codes
        );
    }, [filterOptions.equipmentTypes, filters.freight_order_specified_eqpt_codes]);

    const selectedPickupParties = useMemo((): PickupParty[] => {
        return getFilterValuesFromParams(
            filterOptions.shippers,
            'pickup_party_id',
            filters.pickup_party_ids
        );
    }, [filterOptions.shippers, filters.pickup_party_ids]);

    const selectedDeliveryParties = useMemo((): DeliveryParty[] => {
        return getFilterValuesFromParams(
            filterOptions.consignees,
            'delivery_party_id',
            filters.delivery_party_ids
        );
    }, [filterOptions.consignees, filters.delivery_party_ids]);

    const selectedCommodities = useMemo((): Commodity[] => {
        return getFilterValuesFromParams(
            filterOptions.commodities,
            'commodity_code',
            filters.commodity_codes
        );
    }, [filterOptions.commodities, filters.commodity_codes]);

    const formatRevenue = (): string => {
        const format = (amount: number): string => {
            return amount >= 1000 ? `$${amount / 1000}k` : `$${amount}`;
        };

        if (filters.minimum_revenue_usd_amount !== undefined && filters.minimum_revenue_usd_amount !== null &&
            filters.maximum_revenue_usd_amount !== undefined && filters.maximum_revenue_usd_amount !== null) {
            return `${format(filters.minimum_revenue_usd_amount)} - ${format(filters.maximum_revenue_usd_amount)}`;
        }

        if (filters.minimum_revenue_usd_amount !== undefined && filters.minimum_revenue_usd_amount !== null) {
            return `More than ${format(filters.minimum_revenue_usd_amount)}`;
        }

        if (filters.maximum_revenue_usd_amount !== undefined && filters.maximum_revenue_usd_amount !== null) {
            return `$0 - ${format(filters.maximum_revenue_usd_amount)}`;
        }

        return '';
    };

    return (
        <StyledDiv className={classes.filtersDisplay}>
            <ActiveFilterChip
                tooltip='Date Range'
                label={`${apiDateToLocalDate(
                    filters.earliest_delivery_departure_timestamp
                ).toLocaleDateString()} - ${apiDateToLocalDate(
                    filters.latest_delivery_departure_timestamp
                ).toLocaleDateString()}`}
                icon={mdiCalendarRange}
            />
            {
                formatRevenue() !== '' &&
                <ActiveFilterChip
                    tooltip='Revenue Filter'
                    label={formatRevenue()}
                    icon={mdiCurrencyUsd}
                />
            }
            {
                selectedPickupParties?.map((pickupParty, index) => {
                    return (
                        <ActiveFilterChip
                            key={`shipper-filter-${index}`}
                            tooltip='Shipper'
                            label={truncateId(pickupParty.pickup_party_name)}
                            icon={mdiFactory}
                        />
                    );
                })
            }
            {
                selectedCommodities?.map((commodity, index) => {
                    return (
                        <ActiveFilterChip
                            key={`commodity-filter-${index}`}
                            tooltip='Commodity'
                            label={commodity.commodity_code}
                            icon={mdiPackageVariantClosed}
                        />
                    );
                })
            }
            {
                selectedEquipmentTypes?.map((equipmentType, index) => {
                    return (
                        <ActiveFilterChip
                            key={`eqType-filter-${index}`}
                            tooltip='Equipment Type'
                            label={equipmentType.freight_order_specified_eqpt_code}
                            icon={mdiTruckCargoContainer}
                        />
                    );
                })
            }
            {
                selectedDeliveryParties?.map((deliveryParty, index) => {
                    return (
                        <ActiveFilterChip
                            key={`consignee-filter-${index}`}
                            tooltip='Consignee'
                            label={truncateId(deliveryParty.delivery_party_name)}
                            icon={mdiDrawPen}
                        />
                    );
                })
            }
            {
                selectedCarriers?.map((carrierCode, index) => {
                    return (
                        <ActiveFilterChip
                            key={`carrierCode-filter-${index}`}
                            tooltip='Business Unit'
                            label={carrierCode.carrier_business_unit_name}
                            icon={mdiDomain}
                        />
                    );
                })
            }
        </StyledDiv>
    );
};

export default ActiveFilters;
